import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { Trans, useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { AudioMuteButton } from "@/components/audio-mute-button";
import { LanguagePicker } from "@/components/language-picker";
import { BackButton, HeaderUserDetails } from "@/components/page";

// The one header of the payment flow and the start guides: which machine
// we're paying for / starting, and what to do now. It stays put between steps;
// only the title changes (and slides in). While the machine is being chosen,
// its badge's place is an empty slot; the tapped card flies into it.

export type FlowMachineKind = "washer" | "dryer";

export type FlowMachine = {
  // the number on the machine (`storePlace`)
  number: number;
  kind: FlowMachineKind;
  // kg
  capacity: number;
  // pet machines get their own color
  furFriendly?: boolean;
};

/** The chosen machine's color: on its header badge, the label next to it and
 * its card in the machine list. */
export type MachineAccent = "washer" | "dryer" | "pet";

export function machineAccent(machine: {
  kind: FlowMachineKind;
  furFriendly?: boolean;
}): MachineAccent {
  return machine.furFriendly ? "pet" : machine.kind;
}

const ACCENT_BG = {
  washer: "bg-bub-orange",
  dryer: "bg-bub-dryer",
  pet: "bg-bub-pet",
} as const satisfies Record<MachineAccent, string>;

export type FlowHeaderTone = "default" | "running";

// Pulls the content below the header up into the white dip of its wave.
export const FLOW_HEADER_OVERLAP = "-mt-[80px]";

// The back arrow sits top left in the header, like on the other screens, but
// the step below decides what it does: `FlowBack` renders into the header's slot.
const BackSlotContext = createContext<{
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
} | null>(null);

/** Wraps a page with a `FlowHeader`, so the steps under it can use `FlowBack`. */
export function FlowBackSlot({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <BackSlotContext.Provider value={{ slot, setSlot }}>
      {children}
    </BackSlotContext.Provider>
  );
}

/** The header's back arrow, for the step that's shown. */
export function FlowBack({
  onClick,
  label,
}: {
  onClick: () => void;
  // defaults to "Vissza"
  label?: string;
}) {
  const { t } = useTranslation();
  const slot = useContext(BackSlotContext)?.slot;
  return slot
    ? createPortal(
        <BackButton label={label ?? t("Vissza")} onClick={onClick} />,
        slot,
      )
    : null;
}

export function FlowHeader({
  kind,
  machine,
  programMinutes,
  badgeRef,
  eyebrow,
  title,
  titleKey,
  tone = "default",
  actions,
  overlap = true,
}: {
  kind: FlowMachineKind;
  // null while it's being chosen
  machine: FlowMachine | null;
  // the dryer program's length, once chosen
  programMinutes?: number;
  // gets the machine's badge when it shows up (the card flies into it)
  badgeRef?: Ref<HTMLDivElement>;
  // small status next to the machine type, e.g. "Sikeres fizetés!"
  eyebrow?: ReactNode;
  title: ReactNode;
  // changes with the title, so the new one slides in
  titleKey: string;
  tone?: FlowHeaderTone;
  // top right: language picker, mute, logged in user
  actions?: ReactNode;
  // false: the content starts below the wave, and steps that want to reach
  // into it use `FLOW_HEADER_OVERLAP` themselves
  overlap?: boolean;
}) {
  const setBackSlot = useContext(BackSlotContext)?.setSlot;
  return (
    // Grows with its content on short monitors, so the wave stays below it.
    // The page's content starts up in the white dip of the wave (the content
    // is centered, the wave is lowest at the sides), so pages using this
    // header need a `relative` main to paint over it.
    <header
      className={cn(
        "relative shrink-0 bg-[url('/background-wave-top.svg')] bg-no-repeat bg-cover bg-bottom min-h-[25vh] pb-[110px] px-12 pt-6",
        overlap && "-mb-[80px]",
      )}
    >
      <div className="flex h-[70px] items-center gap-8">
        {/* a leaving step's arrow stacks under the new one while they slide */}
        <div
          ref={setBackSlot}
          className="mr-auto grid [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:mb-0"
        />
        {actions}
      </div>
      <div className="mt-2 flex items-start gap-8">
        {/* the empty slot stays under the badge: it's what the flying card
            lands on, and the badge covers it once it's there */}
        <div className="grid shrink-0 [&>*]:col-start-1 [&>*]:row-start-1">
          <EmptyMachineSlot />
          {machine && (
            <MachineBadge
              // a new machine is a new badge: the ref flies the card into it
              key={machine.number}
              ref={badgeRef}
              number={machine.number}
              background={badgeBackground(machine, tone)}
            />
          )}
        </div>
        <div className="min-w-0 pt-1">
          <p className="flex items-center gap-5 text-[28px] font-bold !text-white">
            <span
              className={cn(
                "rounded-full px-5 pt-2.5 pb-2 !text-white transition-colors duration-500",
                machine ? badgeBackground(machine, tone) : "bg-white/20",
              )}
            >
              {kind === "washer" ? (
                <Trans>Mosógép</Trans>
              ) : (
                <Trans>Szárítógép</Trans>
              )}
              {machine && ` · ${machine.capacity} kg`}
              {programMinutes && (
                <>
                  {` · ${programMinutes} `}
                  <Trans>perc</Trans>
                </>
              )}
            </span>
            {eyebrow && <span className="!text-white">{eyebrow}</span>}
          </p>
          <p
            key={titleKey}
            className="mt-3 text-[52px] font-fancy font-bold leading-tight !text-white animate-[guide-enter_350ms_ease-out]"
          >
            {title}
          </p>
        </div>
      </div>
    </header>
  );
}

function badgeBackground(machine: FlowMachine, tone: FlowHeaderTone) {
  return tone === "running"
    ? "bg-bub-vegan"
    : ACCENT_BG[machineAccent(machine)];
}

function MachineBadge({
  ref,
  number,
  background,
}: {
  ref?: Ref<HTMLDivElement>;
  number: number;
  background: string;
}) {
  return (
    <div
      ref={ref}
      className={cn(
        "size-[150px] shrink-0 rounded-3xl border-[6px] border-white flex flex-col items-center justify-center shadow-xl transition-colors duration-500",
        background,
      )}
    >
      <span className="text-white text-[22px] font-bold leading-none">
        <Trans>Gép</Trans>
      </span>
      <span className="text-white text-[88px] font-fancy font-bold leading-none mt-2">
        {number}
      </span>
    </div>
  );
}

/** Where the machine's badge will be, before it's chosen. */
function EmptyMachineSlot() {
  return (
    <div className="size-[150px] shrink-0 rounded-3xl border-[6px] border-dashed border-white/60 flex flex-col items-center justify-center">
      <span className="!text-white/70 text-[22px] font-bold leading-none">
        <Trans>Gép</Trans>
      </span>
      <span className="!text-white/70 text-[88px] font-fancy font-bold leading-none mt-2">
        {"?"}
      </span>
    </div>
  );
}

/** Top right of the header in the app: the logged in user, mute, language. */
export function FlowHeaderActions() {
  return (
    <>
      <HeaderUserDetails />
      <AudioMuteButton />
      <LanguagePicker />
    </>
  );
}
